import { isUndefined } from "../utils/index";

export type Tick = {
    previousY: number,
    previousRatio: number,
    previousT: number,
    currentT: number,
    stay: ElementStayInfo
}
type ElementStayInfo = Map<number, Map<number, {
    times: number
}>>
export type Interests = number;
export class IntersectionHelper {
    // intersectionRatio 表示和 页面边缘切割的比率
    // 如果向下滚动，卡片离开viewport，则 intersectionRatio 逐渐减少（和页面切割比率减少）

    // 向下滚动进入
    static SCROLL_DOWN_ENTER = 1 << 0;
    // 向下滚动离开
    // 卡片后 xx%
    static SCROLL_DOWN_LEAVE = 1 << 1;
    // 向上滚动进入
    static SCROLL_UP_ENTER = 1 << 2;
    // 向上滚动离开
    static SCROLL_UP_LEAVE = 1 << 3;
    // rangeStayTime: ElementStayInfo
    stayWeakMap: WeakMap<Element, Tick>
    interests: { [k: number]: (e: IntersectionObserverEntry, t: Tick, a: {
        interests: number;
        range: number[];
    }) => void }
    constructor(public range: number[] = []) {
        // intersection observer 触发的范围
        // 范围对应的时间
        // this.rangeStayTime = new Map();
        // 记录DOM和intersection 信息
        this.stayWeakMap = new WeakMap();
        // 感兴趣的滚动方式
        this.interests = {}
    }
    has(k1, k2) {
        return (k1 & k2) !== 0;
    }
    cleanRangeStayTime(target: Element) {
        const info = this.stayWeakMap.get(target);
        if (isUndefined(info?.stay)) {
            return;
        }
        // const nextTick = Object.assign(info, {
        //     stay: undefined
        // })
        this.stayWeakMap.delete(target);
    }
    getNow() {
        // performance.now 返回毫秒，与秒 1000 进率
        // 1000 等于 1 秒
        const n = window.performance && performance.now && performance.now();
        return n || Date.now();
    }

    register(interest, cb) {
        this.interests[interest] = cb
        return this;
    }
    getNextIndex(point) {
        const r = this.range;
        let idx = 0
        for (; idx < r.length; idx++) {
            if (point <= r[idx]) {
                break;
            }
        }
        return idx
    }
    toFixed(s_num) {
        return Number(s_num).toFixed(2);
    }
    /**
     * Intersection共四种情况
     * 1. 向下滚动进入，上百分比
     * 2. 向下滚动离开，下百分比
     * 3. 向上滚动进入，下百分比
     * 4. 向上滚动离开，上百分比
     * stay埋点将四种情况分为两种 https://bytedance.feishu.cn/docs/doccnprp3yqEhZIf8OsQQBwDE7e?appid=2#YGAqrD
     * 1. 向下滚动进入 + 向上滚动离开 卡片上部分
     * 2. 向下滚动离开 + 向上滚动进入 卡片下部分
     * @param {IntersectionObserverEntry} entries 
     */
    handleIntersect = (entrys: IntersectionObserverEntry[]) => {
        entrys.forEach(entry => {
            const { target, isIntersecting } = entry;
            if (!isIntersecting) return;
            // 将 y 改成 top， y 有历史负担
            // Due to compatibility problems (see below), 
            // it is safest to rely on only properties left, top, right, and bottom.
            const currentY = entry.boundingClientRect.top
            const currentRatio = entry.intersectionRatio
            const info = (this.stayWeakMap.get(target) || {}) as Tick
            let { previousY = -1, previousRatio = 0, currentT = this.getNow(), stay } = info;
            let currentStayRangeMap = stay
            if (isUndefined(stay)) {
                currentStayRangeMap = new Map(); 
            }
            let currInterest = 0
            if (previousY === -1 || currentY < previousY) {
                if (currentRatio > previousRatio && isIntersecting) {
                    currInterest = currInterest | IntersectionHelper.SCROLL_DOWN_ENTER
                } else {
                    currInterest = currInterest | IntersectionHelper.SCROLL_DOWN_LEAVE
                }
            } else if (currentY > previousY && isIntersecting) {
                if (currentRatio < previousRatio) {
                    currInterest = currInterest | IntersectionHelper.SCROLL_UP_ENTER
                } else {
                    currInterest = currInterest | IntersectionHelper.SCROLL_UP_LEAVE;
                }
            }
            const now = this.getNow();
            const currentTick: Tick = {
                previousY: currentY,
                previousRatio: currentRatio,
                previousT: currentT,
                currentT: now,
                stay: currentStayRangeMap
            }
            const elapsedTime = now - currentT;
            const previousIndex = this.getNextIndex(previousRatio)
            const timeInPerSlot = elapsedTime
            this.addStayTimeToSlot(currentStayRangeMap, timeInPerSlot, currInterest, this.range[previousIndex])
            this.stayWeakMap.set(target, currentTick)
            for (const [key, cb] of Object.entries(this.interests)) {
                const index = +key;
                if (this.has(index, currInterest)) {
                    cb && cb(entry, currentTick, {
                        interests: currInterest,
                        range: this.range
                    })
                }
            }
        })
    }
    /**
     * 在数组的range里迭代
     * @param {Array<any>} target 要迭代的目标数组
     * @param {number} start 开始index，included
     * @param {number} end 结束index，excluded
     * @param {Function} cb 回调函数
     */
    iteratorInRange(start, end, cb) {
        for (let idx = start; idx < end; idx++) {
            cb && cb(idx)
        }
    }
    addStayTimeToSlot(rangeStayTime, totalTime, scrollStatus, currentTriggerPoint) {
        // 存放 range triggerPoint 和 times 停留时常的映射
        // rangeMap<number, {
        //  times: number   
        // }>
        if(typeof currentTriggerPoint === 'undefined') {
            // 这里说明触发的位置不是我们要收集的，比如触发了1，但最大只要0.7，这样忽略就行
            return;
        }
        const rangeMap = rangeStayTime.get(scrollStatus) || new Map();
        const { times = 0 } = rangeMap.get(currentTriggerPoint) || {}
        
        rangeMap.set(currentTriggerPoint, {
            times: totalTime + times
        })
        rangeStayTime.set(scrollStatus, rangeMap);
    }
    isNullOrUndefined(target, key) {
        return target[key] === null || typeof target[key] === 'undefined'
    }
    initEnhancedIntersectionObserver() {
        // 只关心上半部分展现
        // 可作为示例
        // const topSlice = IntersectionHelper.SCROLL_DOWN_ENTER | IntersectionHelper.SCROLL_UP_LEAVE
        // this.register(topSlice, () => {
        //     // console.log(args, this.enhancedObserver);
        // })
        return args => {
            try{
                this.handleIntersect(args)
            }catch(e) {}
        };
      }
    enhancedViewStay(target: Element) {
        var collected = {}
        try{
              // 这里从 IntersectionHelper HashMap 提取数据，返回一个 {k:v}
              const { SCROLL_DOWN_ENTER, SCROLL_UP_LEAVE } = IntersectionHelper
              const stayMap = this.stayWeakMap.get(target)?.stay
              if (isUndefined(stayMap)) {
                  return;
              }
              for (const [k, v] of stayMap!.entries()) {
                  let direction = 'bottom';
                  if (k === SCROLL_DOWN_ENTER || k === SCROLL_UP_LEAVE) {
                      direction = 'top';
                  }
                  v.forEach(({ times }, threshHold) => {
                      let prev
                      collected[`${threshHold}_${direction}`] = prev = collected[`${threshHold}_${direction}`] || 0;
                      collected[`${threshHold}_${direction}`] += (prev + times);
                  })
              }
              // 用完数据要清空
              this.cleanRangeStayTime(target);
              return collected;
        }finally {
          return collected;
        }
    }
}